import {
  EVENT_TYPE_DEFS,
  type EventSource,
  REPORT_TYPE_KINDS,
  REPORT_TYPES,
  reportTemplateFields,
  type VoyageReportKind,
} from '@felix/domain';
import { Select } from '@mantine/core';
import { IconListDetails, IconTimelineEvent } from '@tabler/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '../../PageHeader.js';
import { Panel } from '../../Panel.js';
import { StatusBadge } from '../../StatusBadge.js';
import table from '../materials/Materials.module.css';
import { useReportLabels } from './report-labels.js';
import classes from './VoyageRecordScreens.module.css';

/** Kaynak alanın çeviri anahtarı: `nor.tenderedAt` → `nor_tenderedAt`. */
const sourceKey = (source: EventSource) => source.field.replaceAll('.', '_');

/**
 * Voyage ▸ Reference Data ▸ Event Types (ADR 0082 Ek §1): raporlarda kaydedilen olaylar, kategori,
 * kaynak rapor ve alan. Salt okunur; domain kataloğundan (`EVENT_TYPE_DEFS`). Olay ayrıca girilmez,
 * raporun zaman alanıdır. Gemi ve ofiste aynı.
 */
export function EventTypesScreen() {
  const { t } = useTranslation();
  const labels = useReportLabels();
  const k = (key: string) => t(`voyage.record.eventTypesPage.${key}`);
  const source = (s: EventSource) =>
    `${labels.typeLabel(s.kind)} · ${t(`voyage.record.sourceFields.${sourceKey(s)}`)}${
      s.schema === 1 ? ` (${k('schema1')})` : ''
    }`;
  return (
    <div className={classes.reference}>
      <PageHeader
        title={k('title')}
        description={k('description')}
        icon={<IconTimelineEvent size={28} stroke={1.6} />}
      />
      <Panel title={k('table')} flush>
        <section
          className={`${table.tableWrap} ${classes.tableBox}`}
          aria-label={k('table')}
          // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
          tabIndex={0}
        >
          <table className={`${table.table} ${classes.table}`}>
            <thead>
              <tr>
                <th scope="col" className={classes.colNo}>
                  #
                </th>
                <th scope="col">{k('event')}</th>
                <th scope="col">{k('category')}</th>
                <th scope="col">{k('source')}</th>
              </tr>
            </thead>
            <tbody>
              {EVENT_TYPE_DEFS.map((def, index) => (
                <tr key={def.type}>
                  <td className={classes.colNo}>{index + 1}</td>
                  <th scope="row">
                    <span className={classes.event} data-category={def.category}>
                      {t(`voyage.record.eventTypes.${def.type}`)}
                    </span>
                  </th>
                  <td>{t(`voyage.record.categories.${def.category}`)}</td>
                  <td className={classes.wrap}>
                    {def.sources.map((s) => (
                      <div key={`${s.kind}:${s.field}:${s.schema ?? ''}`}>{source(s)}</div>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </Panel>
      <p className={classes.note}>{k('note')}</p>
    </div>
  );
}

/**
 * Voyage ▸ Reference Data ▸ Report Templates (ADR 0082 Ek §5; sahip kararı 2026-10-19): seçilen
 * rapor türünün alanları, türleri ve zorunlulukları. Salt okunur; güncel rapor şemasından türetilir
 * (`reportTemplateFields`), ayrı şablon kaydı yok. Gemi ve ofiste aynı.
 */
export function ReportTemplatesScreen() {
  const { t } = useTranslation();
  const labels = useReportLabels();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`voyage.record.templates.${key}`, options);
  const [kind, setKind] = useState<VoyageReportKind>('departure');
  const fields = reportTemplateFields(kind);
  const required = fields.filter((f) => f.required).length;
  return (
    <div className={classes.reference}>
      <PageHeader
        title={k('title')}
        description={k('description')}
        icon={<IconListDetails size={28} stroke={1.6} />}
      />
      <Panel flush>
        <div className={classes.picker}>
          <Select
            label={k('reportType')}
            data={REPORT_TYPES.map((type) => ({
              group: t(`voyage.allReports.types.${type}`),
              items: REPORT_TYPE_KINDS[type].map((value) => ({
                value,
                label: labels.typeLabel(value, type),
              })),
            }))}
            value={kind}
            allowDeselect={false}
            searchable
            onChange={(value) => value && setKind(value as VoyageReportKind)}
          />
          <p className={classes.note}>{k('count', { count: fields.length, required })}</p>
        </div>
      </Panel>
      <Panel title={labels.typeLabel(kind)} flush>
        <section
          className={`${table.tableWrap} ${classes.tableBox}`}
          aria-label={k('table')}
          // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
          tabIndex={0}
        >
          <table className={`${table.table} ${classes.table}`}>
            <thead>
              <tr>
                <th scope="col" className={classes.colNo}>
                  #
                </th>
                <th scope="col">{k('field')}</th>
                <th scope="col">{k('type')}</th>
                <th scope="col">{k('requirement')}</th>
              </tr>
            </thead>
            <tbody>
              {fields.map((field, index) => (
                <tr key={field.key}>
                  <td className={classes.colNo}>{index + 1}</td>
                  <th scope="row">{t(`voyage.record.templates.fields.${field.key}`)}</th>
                  <td>{k(`types.${field.type}`)}</td>
                  <td>
                    <StatusBadge tone={field.required ? 'info' : 'neutral'}>
                      {field.required ? k('required') : k('optional')}
                    </StatusBadge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </Panel>
      <p className={classes.note}>{k('note')}</p>
    </div>
  );
}
